import { UnionBoxTypes } from "@opendaw/studio-adapters";
import { Arrays, asInstanceOf, assert, ByteArrayInput, isInstanceOf, SetMultimap, UUID } from "@opendaw/lib-std";
import { AudioFileBox, AudioUnitBox, AuxSendBox, SelectionBox, SoundfontFileBox, TrackBox } from "@opendaw/studio-boxes";
import { IndexedBox, PointerField } from "@opendaw/lib-box";
import { AudioUnitOrdering } from "../AudioUnitOrdering";
export var ProjectUtils;
(function (ProjectUtils) {
    const isSameGraph = ({ graph: a }, { graph: b }) => a === b;
    const compareIndex = (a, b) => a.index.getValue() - b.index.getValue();
    ProjectUtils.extractAudioUnits = (audioUnitBoxes, targetProject, options = {}) => {
        assert(Arrays.satisfy(audioUnitBoxes, isSameGraph), "AudioUnits must share the same BoxGraph");
        // TODO Implement include options.
        assert(!options.includeAux && !options.includeBus, "Options are not yet implemented");
        const { boxGraph, editing, masterBusBox, rootBox } = targetProject;
        const dependencies = audioUnitBoxes
            .flatMap(box => Array.from(box.graph.dependenciesOf(box, { alwaysFollowMandatory: true }).boxes))
            .filter(box => box.name !== SelectionBox.ClassName && box.name !== AuxSendBox.ClassName);
        const uuidMap = generateTransferMap(audioUnitBoxes, dependencies, rootBox.audioUnits.address.uuid, masterBusBox.address.uuid);
        editing.modify(() => {
            console.debug("inTransaction", boxGraph.inTransaction());
            copy(uuidMap, boxGraph, audioUnitBoxes, dependencies);
            reorderAudioUnits(uuidMap, audioUnitBoxes, rootBox);
            console.debug("inTransaction", boxGraph.inTransaction());
        });
        return audioUnitBoxes.map(source => asInstanceOf(rootBox.graph
            .findBox(uuidMap.get(source.address.uuid).target)
            .unwrap("Target Track has not been copied"), AudioUnitBox));
    };
    ProjectUtils.extractRegions = (regionBoxes, targetProject, insertPosition = 0.0) => {
        assert(Arrays.satisfy(regionBoxes, isSameGraph), "Region smust be from the same BoxGraph");
        const regionBoxSet = new Set(regionBoxes);
        const trackBoxSet = new Set();
        const audioUnitBoxSet = new SetMultimap();
        // Collect AudioUnits and Tracks
        regionBoxes.forEach(regionBox => {
            const trackBox = asInstanceOf(regionBox.regions.targetVertex.unwrap().box, TrackBox);
            trackBoxSet.add(trackBox);
            const audioUnitBox = asInstanceOf(trackBox.tracks.targetVertex.unwrap().box, AudioUnitBox);
            audioUnitBoxSet.add(audioUnitBox, trackBox);
        });
        console.debug(`Found ${audioUnitBoxSet.keyCount()} audioUnits`);
        console.debug(`Found ${trackBoxSet.size} tracks`);
        const audioUnitBoxes = [...audioUnitBoxSet.keys()];
        const { boxGraph, editing, masterBusBox, rootBox } = targetProject;
        const excludeBox = box => (isInstanceOf(box, TrackBox) && !trackBoxSet.has(box))
            || (UnionBoxTypes.isRegionBox(box) && !regionBoxSet.has(box));
        const dependencies = audioUnitBoxes
            .flatMap(box => Array.from(box.graph.dependenciesOf(box, { excludeBox, alwaysFollowMandatory: true }).boxes))
            .filter(box => box.name !== SelectionBox.ClassName && box.name !== AuxSendBox.ClassName);
        const uuidMap = generateTransferMap(audioUnitBoxes, dependencies, rootBox.audioUnits.address.uuid, masterBusBox.address.uuid);
        editing.modify(() => {
            copy(uuidMap, boxGraph, audioUnitBoxes, dependencies);
            reorderAudioUnits(uuidMap, audioUnitBoxes, rootBox);
            // reorder track indices
            audioUnitBoxSet.forEach((_, trackBoxes) => [...trackBoxes]
                .sort(compareIndex)
                .forEach((source, index) => {
                const box = boxGraph
                    .findBox(uuidMap.get(source.address.uuid).target)
                    .unwrap("Target Track has not been copied");
                asInstanceOf(box, TrackBox).index.setValue(index);
            }));
            // move new regions to the target position
            const minPosition = regionBoxes.reduce((min, region) => Math.min(min, region.position.getValue()), Number.MAX_VALUE);
            const delta = insertPosition - minPosition;
            regionBoxes.forEach((source) => {
                const box = boxGraph
                    .findBox(uuidMap.get(source.address.uuid).target)
                    .unwrap("Target Track has not been copied");
                const position = UnionBoxTypes.asRegionBox(box).position;
                position.setValue(position.getValue() + delta);
            });
        });
    };
    const generateTransferMap = (audioUnitBoxes, dependencies, rootBoxUUID, masterBusBoxUUID) => {
        const uuidMap = UUID.newSet(({ source }) => source);
        uuidMap.addMany([
            ...audioUnitBoxes
                .filter(({ output: { targetAddress } }) => targetAddress.nonEmpty())
                .map(box => ({
                source: box.output.targetAddress.unwrap().uuid,
                target: masterBusBoxUUID
            })),
            ...audioUnitBoxes
                .map(box => ({
                source: box.collection.targetAddress.unwrap("AudioUnitBox was not connected to a RootBox").uuid,
                target: rootBoxUUID
            })),
            ...audioUnitBoxes
                .map(box => ({
                source: box.address.uuid,
                target: UUID.generate()
            })),
            ...dependencies
                .map(({ address: { uuid }, name }) => ({
                source: uuid,
                target: name === AudioFileBox.ClassName || name === SoundfontFileBox.ClassName
                    ? uuid
                    : UUID.generate()
            }))
        ]);
        return uuidMap;
    };
    const copy = (uuidMap, boxGraph, audioUnitBoxes, dependencies) => {
        PointerField.decodeWith({
            map: (_pointer, newAddress) => newAddress.map(address => uuidMap.opt(address.uuid).match({
                none: () => address,
                some: ({ target }) => address.moveTo(target)
            }))
        }, () => {
            audioUnitBoxes
                .forEach((source) => {
                const input = new ByteArrayInput(source.toArrayBuffer());
                const key = source.name;
                const uuid = uuidMap.get(source.address.uuid).target;
                boxGraph.createBox(key, uuid, box => box.read(input));
            });
            dependencies
                .forEach((source) => {
                const input = new ByteArrayInput(source.toArrayBuffer());
                const key = source.name;
                const uuid = uuidMap.get(source.address.uuid).target;
                if (source instanceof AudioFileBox || source instanceof SoundfontFileBox) {
                    // Those boxes keep their UUID. So if they are already in the graph, we can just read them.
                    if (boxGraph.findBox(source.address.uuid).nonEmpty()) {
                        source.read(input);
                        return;
                    }
                }
                boxGraph.createBox(key, uuid, box => box.read(input));
            });
        });
    };
    const reorderAudioUnits = (uuidMap, audioUnitBoxes, rootBox) => audioUnitBoxes
        .toSorted(compareIndex)
        .map(source => ({
        source, target: asInstanceOf(rootBox.graph
            .findBox(uuidMap.get(source.address.uuid).target)
            .unwrap("Target Track has not been copied"), AudioUnitBox)
    }))
        .forEach(({ source, target }) => {
        const boxes = IndexedBox.collectIndexedBoxes(rootBox.audioUnits, AudioUnitBox);
        const sourceExistsInTargetGraph = source.collection.targetVertex.contains(rootBox.audioUnits);
        if (sourceExistsInTargetGraph) {
            const newIndex = source.index.getValue() + 1;
            for (let i = newIndex; i < boxes.length; i++) {
                boxes[i].index.setValue(i);
            }
            target.index.setValue(newIndex);
        }
        else {
            // We append new audioUnits to the end of the list.
            // That is a bit slow, but we are probably not dealing with too many audioUnits at this point.
            const order = AudioUnitOrdering[target.type.getValue()];
            let index = 0 | 0;
            for (; index < boxes.length; index++) {
                if (AudioUnitOrdering[boxes[index].type.getValue()] > order) {
                    break;
                }
            }
            const insertIndex = index;
            while (index < boxes.length) {
                boxes[index].index.setValue(++index);
            }
            target.index.setValue(insertIndex);
        }
    });
})(ProjectUtils || (ProjectUtils = {}));
